// Browser chrome: tab strip, toolbar with address bar, mobile bottom bar, tab overview.
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { ArrowLeft, ArrowRight, Globe, LogOut, Maximize, Mic, Monitor, MoreHorizontal, Plus, RotateCw, Settings, TriangleAlert, Video, VolumeX, Volume2, X, Keyboard } from 'lucide-react';
import { t, tKind } from '../core/i18n';
import type { MediaKind, Tab } from '../core/protocol';
import { normalizeInput } from '../core/url';

const tabTitle = (tab: Tab) => (tab.title && tab.title !== 'about:blank' ? tab.title : t('newTab'));

function Favicon({ tab }: { tab: Tab }) {
  if (tab.loading) return <span className="spinner" />;
  if (tab.favicon) return <img src={tab.favicon} alt="" />;
  return <Globe size={14} />;
}

export function TabStrip(p: {
  tabs: Tab[];
  active: string;
  muted: boolean;
  onActivate: (id: string) => void;
  onClose: (id: string) => void;
  onNew: () => void;
  onUnmute: () => void;
}) {
  return (
    <div className="tabstrip" role="tablist">
      <div className="tabs">
        {p.tabs.map((tab) => {
          const active = tab.id === p.active;
          return (
            <div
              key={tab.id}
              role="tab"
              aria-selected={active}
              className={'tab' + (active ? ' active' : '')}
              title={tabTitle(tab)}
              onClick={() => p.onActivate(tab.id)}
              onMouseDown={(e) => {
                if (e.button === 1) {
                  e.preventDefault();
                  p.onClose(tab.id);
                }
              }}
            >
              <span className="tab-favicon">
                <Favicon tab={tab} />
              </span>
              <span className="tab-title">{tabTitle(tab)}</span>
              {active && p.muted && (
                <button
                  className="icon-btn"
                  aria-label={t('unmute')}
                  title={t('unmute')}
                  onClick={(e) => {
                    e.stopPropagation();
                    p.onUnmute();
                  }}
                >
                  <VolumeX size={13} />
                </button>
              )}
              <button
                className="icon-btn tab-close"
                aria-label={t('closeTab')}
                onClick={(e) => {
                  e.stopPropagation();
                  p.onClose(tab.id);
                }}
              >
                <X size={13} />
              </button>
            </div>
          );
        })}
      </div>
      <button className="icon-btn new-tab" aria-label={t('newTab')} title={t('newTab')} onClick={p.onNew}>
        <Plus size={16} />
      </button>
    </div>
  );
}

const MEDIA_ICON: Record<MediaKind, typeof Video> = { camera: Video, microphone: Mic, screen: Monitor };

function splitUrl(url: string) {
  try {
    const u = new URL(url);
    if (u.protocol !== 'http:' && u.protocol !== 'https:') return { host: url, rest: '', http: false };
    const host = u.host.replace(/^www\./, '');
    const rest = (u.pathname === '/' ? '' : u.pathname) + u.search + u.hash;
    return { host, rest, http: u.protocol === 'http:' };
  } catch {
    return { host: url, rest: '', http: false };
  }
}

/** Address bar: shows "host/path" with the host emphasized; full URL while editing. */
export function Omnibox(p: { url: string; focusSignal: number; activeMedia: MediaKind[]; onNavigate: (url: string) => void; onStopMedia: (k: MediaKind) => void }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState('');
  const input = useRef<HTMLInputElement>(null);
  const blank = !p.url || p.url === 'about:blank';

  const startEditing = () => {
    setValue(blank ? '' : p.url);
    setEditing(true);
  };
  useEffect(() => {
    if (p.focusSignal) startEditing();
  }, [p.focusSignal]);
  useEffect(() => {
    if (editing) {
      input.current?.focus();
      input.current?.select();
    }
  }, [editing]);

  const { host, rest, http } = splitUrl(p.url);

  return (
    <form
      className="omnibox"
      onSubmit={(e) => {
        e.preventDefault();
        if (!value.trim()) return;
        p.onNavigate(normalizeInput(value));
        setEditing(false);
      }}
    >
      {!editing && http && (
        <span className="not-secure" title={t('notSecure')}>
          <TriangleAlert size={13} />
          {t('notSecure')}
        </span>
      )}
      {editing ? (
        <input
          ref={input}
          value={value}
          placeholder={t('addressPlaceholder')}
          spellCheck={false}
          autoCapitalize="off"
          autoCorrect="off"
          inputMode="url"
          enterKeyHint="go"
          onChange={(e) => setValue(e.target.value)}
          onBlur={() => setEditing(false)}
          onKeyDown={(e) => e.key === 'Escape' && setEditing(false)}
        />
      ) : (
        <div className="display" onClick={startEditing} role="textbox" aria-label={t('addressPlaceholder')} tabIndex={0} onFocus={startEditing}>
          {blank ? (
            <span className="placeholder">{t('addressPlaceholder')}</span>
          ) : (
            <>
              <span className="url-host">{host}</span>
              <span className="url-rest">{rest}</span>
            </>
          )}
        </div>
      )}
      {p.activeMedia.map((k) => {
        const Icon = MEDIA_ICON[k];
        const label = `${tKind(k === 'screen' ? 'display-capture' : k)} ${t('active')} – ${t('stopSharing')}`;
        return (
          <button key={k} type="button" className="media-indicator" title={label} aria-label={label} onMouseDown={(e) => e.preventDefault()} onClick={() => p.onStopMedia(k)}>
            <Icon size={14} />
          </button>
        );
      })}
    </form>
  );
}

/** Growing progress line; completes and fades when loading ends. */
export function ProgressLine({ loading }: { loading: boolean }) {
  const [state, setState] = useState<'idle' | 'loading' | 'done'>('idle');
  useEffect(() => {
    if (loading) {
      setState('idle');
      const id = requestAnimationFrame(() => setState('loading'));
      return () => cancelAnimationFrame(id);
    }
    setState((s) => (s === 'loading' ? 'done' : s));
  }, [loading]);
  return <div className={'progress-line ' + state} />;
}

export interface MenuItem {
  label: string;
  icon: typeof Video;
  onClick: () => void;
}

export interface MenuProps {
  extraItems?: MenuItem[];
  muted: boolean;
  onToggleMute: () => void;
  onSettings: () => void;
  onLogout: () => void;
  up?: boolean;
}

export function MainMenu(p: MenuProps) {
  const [open, setOpen] = useState(false);
  const item = (fn: () => void) => () => {
    setOpen(false);
    fn();
  };
  return (
    <div className="menu-wrap">
      <button className="icon-btn" aria-label={t('menu')} title={t('menu')} onClick={() => setOpen((o) => !o)}>
        <MoreHorizontal size={16} />
      </button>
      {open && (
        <>
          <div className="menu-backdrop" onClick={() => setOpen(false)} />
          <div className={'menu' + (p.up ? ' up' : '')} role="menu">
            {p.extraItems?.map((x) => (
              <button key={x.label} role="menuitem" onClick={item(x.onClick)}>
                <x.icon size={15} /> {x.label}
              </button>
            ))}
            {!!p.extraItems?.length && <hr />}
            <button role="menuitem" onClick={item(p.onToggleMute)}>
              {p.muted ? <Volume2 size={15} /> : <VolumeX size={15} />} {p.muted ? t('unmute') : t('mute')}
            </button>
            {document.fullscreenEnabled && (
              <button
                role="menuitem"
                onClick={item(() => (document.fullscreenElement ? document.exitFullscreen() : document.documentElement.requestFullscreen().catch(() => {})))}
              >
                <Maximize size={15} /> {t('fullscreen')}
              </button>
            )}
            <button role="menuitem" onClick={item(p.onSettings)}>
              <Settings size={15} /> {t('settings')}
            </button>
            <hr />
            <button role="menuitem" onClick={item(p.onLogout)}>
              <LogOut size={15} /> {t('logout')}
            </button>
          </div>
        </>
      )}
    </div>
  );
}

export interface ToolbarProps extends MenuProps {
  siteInfo?: ReactNode;
  tab?: Tab;
  focusSignal: number;
  activeMedia: MediaKind[];
  onNavigate: (url: string) => void;
  onBack: () => void;
  onForward: () => void;
  onReload: () => void;
  onStop: () => void;
  onStopMedia: (k: MediaKind) => void;
}

export function Toolbar(p: ToolbarProps) {
  return (
    <div className="toolbar">
      <button className="icon-btn" aria-label={t('back')} title={t('back')} disabled={!p.tab?.canGoBack} onClick={p.onBack}>
        <ArrowLeft size={16} />
      </button>
      <button className="icon-btn" aria-label={t('forward')} title={t('forward')} disabled={!p.tab?.canGoForward} onClick={p.onForward}>
        <ArrowRight size={16} />
      </button>
      {p.tab?.loading ? (
        <button className="icon-btn" aria-label={t('stop')} title={t('stop')} onClick={p.onStop}>
          <X size={16} />
        </button>
      ) : (
        <button className="icon-btn" aria-label={t('reload')} title={t('reload')} onClick={p.onReload}>
          <RotateCw size={15} />
        </button>
      )}
      {p.siteInfo}
      <Omnibox url={p.tab?.url ?? ''} focusSignal={p.focusSignal} activeMedia={p.activeMedia} onNavigate={p.onNavigate} onStopMedia={p.onStopMedia} />
      <MainMenu {...p} />
      <ProgressLine loading={!!p.tab?.loading} />
    </div>
  );
}

/** Phone layout: Safari-like bar at the bottom (thumb reach). */
export function BottomBar(p: ToolbarProps & { tabCount: number; onTabs: () => void; onKeyboard: () => void }) {
  return (
    <div className="bottombar">
      <ProgressLine loading={!!p.tab?.loading} />
      <button className="icon-btn" aria-label={t('back')} disabled={!p.tab?.canGoBack} onClick={p.onBack}>
        <ArrowLeft size={18} />
      </button>
      {p.siteInfo}
      <Omnibox url={p.tab?.url ?? ''} focusSignal={p.focusSignal} activeMedia={p.activeMedia} onNavigate={p.onNavigate} onStopMedia={p.onStopMedia} />
      <button className="icon-btn" aria-label={t('keyboard')} onClick={p.onKeyboard}>
        <Keyboard size={18} />
      </button>
      <button className="icon-btn" aria-label={t('tabs')} onClick={p.onTabs}>
        <span className="tab-count">{p.tabCount}</span>
      </button>
      <MainMenu {...p} up />
    </div>
  );
}

export function TabSheet(p: { tabs: Tab[]; active: string; onActivate: (id: string) => void; onClose: (id: string) => void; onNew: () => void; onDone: () => void }) {
  return (
    <div className="tab-sheet" role="dialog" aria-label={t('tabs')}>
      <div className="tab-sheet-list">
        {p.tabs.length === 0 && <div className="empty">{t('noneOpen')}</div>}
        {p.tabs.map((tab) => (
          <div
            key={tab.id}
            className={'tab-sheet-item' + (tab.id === p.active ? ' active' : '')}
            onClick={() => {
              p.onActivate(tab.id);
              p.onDone();
            }}
          >
            <span className="tab-favicon">
              <Favicon tab={tab} />
            </span>
            <div className="t">
              <span>{tabTitle(tab)}</span>
              <span>{tab.url && tab.url !== 'about:blank' ? splitUrl(tab.url).host : ''}</span>
            </div>
            <button
              className="icon-btn"
              aria-label={t('closeTab')}
              onClick={(e) => {
                e.stopPropagation();
                p.onClose(tab.id);
              }}
            >
              <X size={16} />
            </button>
          </div>
        ))}
      </div>
      <div className="tab-sheet-foot">
        <button
          className="icon-btn"
          aria-label={t('newTab')}
          onClick={() => {
            p.onNew();
            p.onDone();
          }}
        >
          <Plus size={20} />
        </button>
        <button className="btn" onClick={p.onDone}>
          {t('done')}
        </button>
      </div>
    </div>
  );
}
